import { useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Pagination } from '@web/components/pagination/pagination';

import { useGlobalContext } from '@web/layouts/page/context';

import { useAuthenticatedCRMRequestConfig } from '@web/services/crm-api/hooks';
import { EventDto, GetEventsByFilterParams, RetrieveType, useGetMemberEventsByFilter } from '@web/services/crm-api/rest';

import { TimeFilter } from '@web/templates/user-events-overview-page';

import { axiosParamsSerializer } from '@web/utils/axios-params-serializer';

interface useMemberEventsSearchProps {
	initialValue?: EventDto[];
	timeframe: TimeFilter;
	limit: number;
}

export function useMemberEventsSearch({ initialValue, timeframe, limit }: useMemberEventsSearchProps) {
	const [total, setTotal] = useState(initialValue?.length || 0);
	const pagination = Pagination.usePagination<EventDto>({ limit, total });
	const { preview } = useGlobalContext();
	const { memberId, isMember, requestConfig, status } = useAuthenticatedCRMRequestConfig();

	const initialQueryParams: GetEventsByFilterParams = {
		sort: 'Ascending',
		sortType: 'StartDate',
		dateFilterOperator: timeframe,
		dateFilterDate: new Date().toISOString(),
		dateFilterType: 'StartDate',
		pageSize: pagination.limit,
		page: pagination.page,
		statusCode: preview ? ['Published', 'Cancelled', 'Draft'] : ['Published', 'Cancelled'],
		retrieve: RetrieveType.Registered,
	};

	const [queryParams, updateQueryparams] = useState<GetEventsByFilterParams>(initialQueryParams);

	// Sync pagination to query params
	useEffect(() => {
		updateQueryparams((previousValue) => ({
			...previousValue,
			pageSize: pagination.limit,
			page: pagination.page,
		}));
	}, [pagination.limit, pagination.page, timeframe]);

	useEffect(() => {
		pagination.firstPage();
		updateQueryparams((prevValue) => ({
			...prevValue,
			dateFilterOperator: timeframe,
			// Go back to page 1 when changing tabs
			page: pagination.page,
		}));
	// Cannot use pagination.page in the dependancies of the useEffect or else it will trigger everytime we change pages and it will snap back to the first page
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeframe]);

	const eventsAuthQuery = useGetMemberEventsByFilter(memberId, { ...queryParams }, {
		query: {
			enabled: isMember && !!memberId,
		},
		request: {
			...requestConfig,
			paramsSerializer: {
				serialize: (params) => axiosParamsSerializer(params),
			},
		},
	});

	useDeepCompareEffect(() => {
		if (status === 'authenticated' && !eventsAuthQuery.isFetching && isMember) {
			eventsAuthQuery.refetch();
		}
	}, [eventsAuthQuery.refetch, status, queryParams]);

	useEffect(() => {
		if (typeof eventsAuthQuery.data?.meta?.total === 'number') {
			setTotal(eventsAuthQuery.data.meta.total);
		}
	}, [eventsAuthQuery.data, eventsAuthQuery.refetch]);

	return {
		eventsAuthQuery,
		pagination,
	};
}
